import { Check, Globe } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useLanguage } from '@/contexts/LanguageContext';
import { LANGUAGES, getLanguageNativeName } from '@/constants/languages';
import type { Language } from '@/i18n/types';

interface WelcomeStepProps {
  onNext: () => void;
}

/**
 * Welcome step that introduces Data Mission and its features during onboarding.
 * Includes a language selector in the footer.
 */
export function WelcomeStep({ onNext }: WelcomeStepProps) {
  const { t, language, setLanguage } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext();
  };

  const handleLanguageChange = (newLanguage: Language) => {
    setLanguage(newLanguage);
  };


  return (
    <Card className="h-[480px] flex flex-col">
      <CardHeader>
        <CardTitle>{t.userOnboardingWizard.welcomeStep.title}</CardTitle>
        <CardDescription>{t.userOnboardingWizard.welcomeStep.description}</CardDescription>
      </CardHeader>
      <form onSubmit={handleSubmit} className="flex flex-col flex-1">
        <CardContent className="space-y-6 flex-1">
          <p className="text-sm text-muted-foreground">
            {t.userOnboardingWizard.welcomeStep.intro}
          </p>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-1.5">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm">{t.userOnboardingWizard.welcomeStep.feature1}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-1.5">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm">{t.userOnboardingWizard.welcomeStep.feature2}</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-1.5">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <p className="text-sm">{t.userOnboardingWizard.welcomeStep.feature3}</p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between pt-4">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="text-muted-foreground">
                <Globe className="h-4 w-4 mr-2" />
                {getLanguageNativeName(language)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem key={lang.code} onClick={() => { handleLanguageChange(lang.code); }}>
                  {lang.nativeName}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button type="submit">{t.userOnboardingWizard.welcomeStep.getStarted}</Button>
        </CardFooter>
      </form>
    </Card>
  );
}
